import {
  Box,
  Card,
  CardContent,
  List,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import PendingActionsOutlined from "@mui/icons-material/PendingActionsOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  DASHBOARD_COMPACT_WIDGET_HEIGHT,
} from "./dashboardWidgetLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import {
  GetWorkInProgressResponse,
  WorkInProgressList,
} from "subModule/src/services/analytics/interface";
import { useGetWorkInProgressService } from "subModule/src/services/analytics/useAnalytics";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
interface WorkInProgressProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}
export default function WorkInProgress(props: WorkInProgressProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px",
  );
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const { refetch: apiRefetch } = useGetWorkInProgressService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });
  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetWorkInProgressResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };
  const maxAge = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return 1;
    return Math.max(...data.map((d: any) => Number(d.Age) || 0), 1);
  }, [data]);
  const getAgeTone = (age: number) => {
    const ratio = age / maxAge;
    if (ratio >= 0.66) return { bg: "#FEE2E2", fg: "#DC2626" };
    if (ratio >= 0.33) return { bg: "#FEF3C7", fg: "#D97706" };
    return { bg: "#DCFCE7", fg: "#16A34A" };
  };
  return (
    <Card
      sx={{
        height: DASHBOARD_COMPACT_WIDGET_HEIGHT,
        minHeight: DASHBOARD_COMPACT_WIDGET_HEIGHT,
        boxShadow: "none",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        "& .MuiCardContent-root": {
          padding: { xs: "12px !important", sm: "16px !important" },
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <CardContent>
        <Box sx={{ ...dashboardWidgetHeaderSx, flexShrink: 0 }}>
          <AnalyticsWidgetHeader
            title={t("workInProgress")}
            icon={<PendingActionsOutlined />}
            iconBg={`${props.themes?.primary || "#4F46E5"}18`}
            iconColor={props.themes?.primary || "#4F46E5"}
          />
          <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                        { position: "absolute", right: 0 },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "workInProgress"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleDropdownActions(
                          "workInProgress",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleDropdownActions(
                            "workInProgress",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleDropdownActions(
                          "workInProgress",
                          "loadNext",
                        );
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      width: screenWidth ? "60px" : "auto",
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ flex: 1, minHeight: 0, mt: 1 }}>
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </Box>
        ) : (
          <List
            dense
            disablePadding
            sx={{
              flex: 1,
              minHeight: 0,
              mt: 1,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {(data && data?.length === 0) || !data ? (
              <Box className="flex justify-center flex-col">
                <Typography
                  className="font-Saira text-center italic font-semibold mt-[40px]"
                  sx={{ color: props.themes.primary }}
                >
                  {t("noRecordFound")}
                </Typography>
              </Box>
            ) : (
              data?.map((item: WorkInProgressList, index: number) => {
                const ageTone = getAgeTone(Number(item.Age) || 0);
                return (
                  <li
                    key={`wip-${index}`}
                    style={{ width: "100%", marginBottom: "6px" }}
                  >
                    <Box
                      className="flex items-center gap-2 px-1.5 py-1.5"
                      sx={{
                        borderRadius: "10px",
                        backgroundColor: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      <Tooltip
                        followCursor
                        title={t("vehiclePlateNo") + ": " + item.VehiclePlateNo}
                      >
                        <Box
                          sx={{
                            px: 1,
                            py: 0.4,
                            borderRadius: "6px",
                            backgroundColor: `${props.themes?.primary || "#4F46E5"}14`,
                            color: props.themes?.primary || "#4F46E5",
                            fontFamily: "Saira, sans-serif",
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            maxWidth: 90,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {item.VehiclePlateNo}
                        </Box>
                      </Tooltip>
                      <Box className="flex-1 min-w-0">
                        <Tooltip
                          followCursor
                          title={
                            t("totalAmount") +
                            ": " +
                            (item?.TotalAmount?.toLocaleString() || 0)
                          }
                        >
                          <Typography
                            className="font-Saira"
                            sx={{
                              fontSize: 12,
                              fontWeight: 800,
                              color: "#1E293B",
                              lineHeight: 1.2,
                            }}
                          >
                            {new Intl.NumberFormat("en", {
                              notation: "compact",
                              compactDisplay: "short",
                              maximumFractionDigits: 2,
                            }).format(Math.abs(item?.TotalAmount || 0))}
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 9,
                                fontWeight: 600,
                                ml: 0.4,
                                textTransform: "uppercase",
                                color: "#94A3B8",
                              }}
                            >
                              {props?.user?.business?.currency || ""}
                            </Typography>
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Tooltip followCursor title={`${t("ages")}: ${item.Age}`}>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.35,
                            borderRadius: "999px",
                            backgroundColor: ageTone.bg,
                            color: ageTone.fg,
                            fontFamily: "Saira, sans-serif",
                            fontSize: 10,
                            fontWeight: 800,
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.Age} {t("days")}
                        </Box>
                      </Tooltip>
                    </Box>
                  </li>
                );
              })
            )}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
